import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDate, fmtMoney } from "../../../lib/format";
import { canPieces, unitPrice } from "../../../lib/uom";

const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const useData = (code) => {
  const customer = useQuery({ queryKey: ["staff-cust", code], retry: false, queryFn: () => api.get(`/customers/${encodeURIComponent(code)}`).then((r) => r.data) });
  const items = useQuery({ queryKey: ["items"], queryFn: () => api.get("/catalogue/items").then((r) => r.data), staleTime: 300000 });
  const prices = useQuery({ queryKey: ["prices", code], retry: false, queryFn: () => api.get("/prices", { params: { customer_code: code } }).then((r) => r.data) });
  const delivery = useQuery({ queryKey: ["ob-delivery", code], queryFn: () => api.get("/delivery/me", { params: { customer_code: code } }).then((r) => r.data) });
  return { customer, items, prices, delivery };
};

const Row = ({ i, p, q, mode, setLine, lang, t }) => (
  <li className={`flex items-center gap-2 px-3 py-2 ${q ? "bg-brand/10" : ""}`} data-testid={`ob-row-${i.code}`}>
    <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-navy">{i[`name_${lang}`]}</p><p className="text-[11px] text-slate-500">{i.code} · {fmtMoney(unitPrice(i, p, mode))} / {t(mode === "piece" ? "x.uom.piece" : "x.uom.box")}</p></div>
    {canPieces(i) && <select value={mode} onChange={(e) => setLine(i.code, q, e.target.value)} className="h-10 rounded-lg border border-slate-200 bg-white px-1 text-xs" data-testid={`ob-mode-${i.code}`}><option value="box">{t("x.uom.box")}</option><option value="piece">{t("x.uom.piece")}</option></select>}
    <input type="number" min={0} inputMode="numeric" value={q || ""} placeholder="0" onChange={(e) => setLine(i.code, Math.max(0, parseInt(e.target.value || "0", 10) || 0), mode)}
      className="h-10 w-14 rounded-lg border border-slate-200 text-center font-bold text-navy" data-testid={`ob-qty-${i.code}`} />
  </li>
);

export default function OrderOnBehalf() {
  const { code } = useParams();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const { customer, items, prices, delivery } = useData(code);
  const [lines, setLines] = useState({});
  const [q, setQ] = useState("");
  const [date, setDate] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [localId] = useState(() => crypto.randomUUID());
  const priceMap = useMemo(() => Object.fromEntries((prices.data || []).map((p) => [p.item_code, p])), [prices.data]);
  const list = useMemo(() => {
    const nq = norm(q.trim());
    return (items.data || []).filter((i) => i.active && priceMap[i.code] && (!nq || norm(`${i.code} ${i.name_el} ${i.name_en}`).includes(nq))).slice(0, 80);
  }, [items.data, priceMap, q]);
  const setLine = (c, qty, mode) => setLines((s) => { const n = { ...s }; if (qty > 0) n[c] = { qty, mode }; else delete n[c]; return n; });
  const byCode = useMemo(() => Object.fromEntries((items.data || []).map((i) => [i.code, i])), [items.data]);
  const total = Object.entries(lines).reduce((s, [c, l]) => s + l.qty * unitPrice(byCode[c], priceMap[c], l.mode), 0);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { local_id: localId, customer_code: code, requested_delivery_date: date, notes, lines: Object.entries(lines).map(([item_code, l]) => ({ item_code, qty_boxes: l.qty, uom: l.mode })) };
      await api.post("/orders/on-behalf", body);
      toast.success(t("x.ob.done"));
      nav(`/staff/customers/${encodeURIComponent(code)}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" && d.startsWith("delivery_date") ? t("x.ob.badDate") : typeof d === "string" && d.startsWith("items_unavailable") ? t("x.ob.badItems") : t("common.error"));
      setBusy(false);
    }
  };

  if (customer.isLoading || items.isLoading || prices.isLoading) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-slate-400" />;
  if (customer.isError || prices.isError) return <p className="rounded-2xl bg-white p-6 text-sm text-rose-700 shadow-sm" data-testid="ob-not-found">{t("x.cust.notFound")}</p>;
  const c = customer.data;
  return (
    <div className="space-y-4" data-testid="order-on-behalf-page">
      <Link to={`/staff/customers/${encodeURIComponent(code)}`} className="inline-flex h-11 items-center gap-1 text-sm text-navy"><ArrowLeft className="h-4 w-4" />{c?.legal_name || c?.name}</Link>
      <h1 className="text-3xl font-bold text-navy">{t("x.ob.title")}</h1>
      <p className="text-sm text-slate-500">{c?.name} · {code} · {t("x.ob.sub")}</p>
      <label className="flex h-12 items-center gap-2 rounded-full bg-white px-4 shadow-sm"><Search className="h-4 w-4 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("shop.searchPh")} className="h-full flex-1 bg-transparent text-base outline-none" data-testid="ob-search" /></label>
      <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl bg-white shadow-sm">{list.map((i) => <Row key={i.code} i={i} p={priceMap[i.code]} q={lines[i.code]?.qty} mode={lines[i.code]?.mode || "box"} setLine={setLine} lang={lang} t={t} />)}</ul>
      <section className="sticky bottom-20 space-y-3 rounded-2xl bg-white p-4 shadow-lg lg:bottom-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("x.ob.date")}</p>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="ob-slots">
          {(delivery.data?.slots || []).map((s) => (
            <button key={s.date} type="button" onClick={() => setDate(s.date)} data-testid={`ob-slot-${s.date}`}
              className={`h-11 shrink-0 rounded-full px-4 text-sm font-semibold ${date === s.date ? "bg-navy text-white" : "bg-canvas text-navy"}`}>{fmtDate(s.date)}</button>
          ))}
          {delivery.data && !delivery.data.slots.length && <p className="text-sm text-slate-500">{t("x.ob.noSlots")}</p>}
        </div>
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("x.ob.notes")} className="h-11 w-full rounded-xl border border-slate-200 px-3" data-testid="ob-notes" />
        <button type="button" disabled={busy || !date || !Object.keys(lines).length} onClick={submit} data-testid="ob-submit" className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand font-semibold text-navy disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("x.ob.submit")} · {Object.keys(lines).length} · {fmtMoney(total)}
        </button>
      </section>
    </div>
  );
}
